import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import VoiceWaveform from './VoiceWaveform';
import { motion } from 'framer-motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import RajDeAudio from '@/assets/audios/Raj_de.mp4';
import RajEnAudio from '@/assets/audios/Raj_en.mp4';
import EmmaDeAudio from '@/assets/audios/emma_de.mp3';
import EmmaEnAudio from '@/assets/audios/emma_en.mp3';
import SarahDeAudio from '@/assets/audios/sarah_de.mp4';
import SarahEnAudio from '@/assets/audios/sarah_en.mp4';
import InboundCustomerEnglishAudio from '@/assets/audios/inboundcustomer_en.wav';
import InboundCustomerGermanAudio from '@/assets/audios/inboundcustomer_de.wav';
import OutboundSalesEnglishAudio from '@/assets/audios/outbound_sales_en.wav';
import OutboundSalesGermanAudio from '@/assets/audios/outbound_sales_de.wav';
import MarketingEnglishAudio from '@/assets/audios/marketing_en.wav';
import MarketingGermanAudio from '@/assets/audios/marketing_de.wav';
import SupportDeskEnglishAudio from '@/assets/audios/supportdesk_en.wav';
import SupportDeskGermanAudio from '@/assets/audios/supportdesk_de.wav';

interface AudioPlayerProps {
  agentName: string;
  agentRole: string;
  avatar: string;
  accent?: string;
  audioType: 'inbound-support' | 'outbound-sales' | 'marketing' | 'support-desk';
}

const voiceSamples: Record<string, Record<'en' | 'de', string>> = {
  Sarah: { en: SarahEnAudio, de: SarahDeAudio },
  Raj: { en: RajEnAudio, de: RajDeAudio },
  Emma: { en: EmmaEnAudio, de: EmmaDeAudio },
};

// Audio type mappings for solutions
const solutionAudios: Record<'inbound-support' | 'outbound-sales' | 'marketing' | 'support-desk', Record<'en' | 'de', string>> = {
  'inbound-support': {
    en: InboundCustomerEnglishAudio,
    de: InboundCustomerGermanAudio,
  },
  'outbound-sales': {
    en: OutboundSalesEnglishAudio,
    de: OutboundSalesGermanAudio,
  },
  'marketing': {
    en: MarketingEnglishAudio,
    de: MarketingGermanAudio,
  },
  'support-desk': {
    en: SupportDeskEnglishAudio,
    de: SupportDeskGermanAudio,
  },
};

const InteractiveAudioPlayer = ({ 
  agentName, 
  agentRole, 
  avatar,
  accent = "American English",
  audioType
}: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'de'>('en');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState([0.8]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioExists, setAudioExists] = useState(false);
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState<string | null>(null);
  const [usingSpeechSynthesis, setUsingSpeechSynthesis] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // First priority: Use agent-specific voice samples (Sarah, Raj, Emma)
  const localizedVoice = voiceSamples[agentName]?.[selectedLanguage];
  // Fallback: Use solution-specific audios for each type (inbound, outbound, marketing, support-desk)
  const solutionAudio = solutionAudios[audioType]?.[selectedLanguage];
  // Final fallback: Use public audio path
  const audioUrl = localizedVoice ?? solutionAudio ?? `/audio/${selectedLanguage}/${audioType}.mp3`;

  // Demo scripts to synthesize if no file or remote fallback is available
  const demoScripts: Record<'en' | 'de', Record<'inbound-support' | 'outbound-sales' | 'marketing' | 'support-desk', string>> = {
    en: {
      'inbound-support': "Thanks for calling Holionix support. I can help with billing, orders, or technical issues. How can I help you today?",
      'outbound-sales': "Hi there, this is Holionix. I noticed your team handles a high volume of calls. Would you like a quick walkthrough of our AI voice agents?",
      'marketing': "Imagine launching campaigns with instant response across all channels. Holionix enables real-time conversations that convert.",
      'support-desk': "Your ticket has been created and prioritized. I can also connect you with live assistance if needed."
    },
    de: {
      'inbound-support': "Danke für Ihren Anruf bei Holionix Support. Ich helfe bei Abrechnung, Bestellungen oder technischen Fragen. Wobei kann ich helfen?",
      'outbound-sales': "Guten Tag, hier ist Holionix. Ihr Team bearbeitet viele Anrufe. Möchten Sie eine kurze Demo unserer KI-Sprachagenten?",
      'marketing': "Stellen Sie sich Kampagnen mit sofortiger Antwort vor. Holionix ermöglicht Echtzeit-Gespräche, die konvertieren.",
      'support-desk': "Ihr Ticket wurde erstellt und priorisiert. Ich kann Sie bei Bedarf auch mit Live-Support verbinden."
    }
  };

  // Temporary remote fallbacks to ensure audio works until local samples are added
  const fallbackAudio: Record<'en' | 'de', Record<'inbound-support' | 'outbound-sales' | 'marketing' | 'support-desk', string>> = {
    en: {
      'inbound-support': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
      'outbound-sales': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
      'marketing': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3',
      'support-desk': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3',
    },
    de: {
      'inbound-support': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3',
      'outbound-sales': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3',
      'marketing': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3',
      'support-desk': 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3',
    },
  };

  // Check if local audio exists; if not, use remote fallback; else enable speech synthesis
  useEffect(() => {
    const checkAudio = async () => {
      setError(null);
      setResolvedAudioUrl(null);
      setUsingSpeechSynthesis(false);
      try {
        const response = await fetch(audioUrl, { method: 'HEAD' });
        if (response.ok) {
          setResolvedAudioUrl(audioUrl);
          setAudioExists(true);
          return;
        }
      } catch {}

      const fallbackUrl = fallbackAudio[selectedLanguage][audioType];
      if (fallbackUrl) {
        try {
          const remoteHead = await fetch(fallbackUrl, { method: 'HEAD' });
          if (remoteHead.ok) {
            setResolvedAudioUrl(fallbackUrl);
            setAudioExists(true);
            return;
          }
        } catch {}
      }

      // As a last resort, we can generate speech using the Web Speech API
      setAudioExists(true);
      setUsingSpeechSynthesis(true);
    };
    checkAudio();
  }, [audioUrl, selectedLanguage, audioType]);

  const handlePlayPause = async () => {
    if (!audioExists) return;
    
    try {
      setError(null);
      if (usingSpeechSynthesis) {
        if (isPlaying) {
          window.speechSynthesis.cancel();
          setIsPlaying(false);
        } else {
          setIsLoading(true);
          const utterance = new SpeechSynthesisUtterance(demoScripts[selectedLanguage][audioType]);
          utterance.rate = 1.0;
          utterance.pitch = 1.0;
          utterance.onend = () => setIsPlaying(false);
          utterance.onerror = () => setIsPlaying(false);
          window.speechSynthesis.speak(utterance);
          setIsPlaying(true);
        }
      } else {
        if (!audioRef.current) return;
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          setIsLoading(true);
          await audioRef.current.play();
          setIsPlaying(true);
        }
      }
    } catch (err) {
      setError('Unable to play audio. Please try again.');
      setIsPlaying(false);
      console.error('Audio playback error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (usingSpeechSynthesis) return;
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlaying(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleError = () => {
      setError('Audio sample coming soon. Professional voice samples will be available shortly.');
      setAudioExists(false);
      setIsPlaying(false);
      setIsLoading(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.volume = volume[0];

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, volume, usingSpeechSynthesis]);

  useEffect(() => {
    if (!usingSpeechSynthesis && audioRef.current) {
      audioRef.current.volume = volume[0];
    }
  }, [volume, usingSpeechSynthesis]);

  useEffect(() => {
    // Reset playback when language changes
    if (!usingSpeechSynthesis && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTime(0);
    } else if (usingSpeechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [selectedLanguage, usingSpeechSynthesis]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect rounded-2xl p-6 space-y-4 hover-lift"
    >
      {!usingSpeechSynthesis && (
        <audio ref={audioRef} src={resolvedAudioUrl || audioUrl} preload="metadata" />
      )}
      
      <div className="flex items-center gap-4 mb-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full gradient-brand p-0.5 animate-pulse-glow">
            <img 
              src={avatar} 
              alt={agentName}
              className="w-full h-full rounded-full object-cover bg-card"
            />
          </div>
          {isPlaying && (
            <div className="absolute -inset-2 rounded-full border-2 border-primary/50 animate-pulse" />
          )}
        </div>
        
        <div className="flex-1">
          <h4 className="font-bold text-lg">{agentName}</h4>
          <p className="text-sm text-muted-foreground">{agentRole}</p>
          <div className="flex items-center gap-2 mt-2">
            <Volume2 className="w-4 h-4 text-accent" />
            <Select 
              value={selectedLanguage} 
              onValueChange={(value: 'en' | 'de') => setSelectedLanguage(value)}
              aria-label="Select voice language"
            >
              <SelectTrigger className="w-32 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Button
          size="lg"
          onClick={handlePlayPause}
          disabled={isLoading || !audioExists}
          className="gradient-brand text-white hover:opacity-90 transition-opacity glow-effect disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={isPlaying ? `Pause ${agentName} voice sample` : `Play ${agentName} voice sample`}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-1" />
          )}
        </Button>
      </div>
      
      <VoiceWaveform isPlaying={isPlaying} bars={30} />
      
      {!usingSpeechSynthesis && duration > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
          <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full gradient-brand transition-all duration-300"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Volume2 className="w-4 h-4 text-muted-foreground" />
        <Slider
          value={volume}
          onValueChange={setVolume}
          max={1}
          step={0.1}
          className="flex-1"
          aria-label="Adjust volume"
        />
      </div>
      
      {error && (
        <div className="text-center space-y-1">
          <p className="text-xs text-amber-500/80" role="alert">
            {error}
          </p>
          <p className="text-[10px] text-muted-foreground/70">
            Professional voice samples in production
          </p>
        </div>
      )}
      
      {!error && audioExists && (
        <p className="text-xs text-muted-foreground text-center" aria-live="polite">
          {usingSpeechSynthesis
            ? (isPlaying ? "Speaking demo voice..." : "Click play for demo voice")
            : (isPlaying ? "Voice sample playing..." : "Click play to listen")}
        </p>
      )}
    </motion.div>
  );
};

export default InteractiveAudioPlayer;
